import {
    AdminSubModule,
    AutomationSubModule,
    C3SubModule,
    InjectionMoldingSubModule,
    Printing3dSubModule,
    MetalsSubModule,
    PlasticsSubModule,
    PrismCommonSubModule,
    PrismModule,
    SettingsSubModule,
} from '../enums/prism-module.enum';
import { UserAbilities } from '../models/user-abilities.model';

// ============================================================================
// RULE: EVERY NEW PERMISSION DEFAULTS TO OFF.  `visible: false`, Can*: false.
//
// This table is what a user gets for any module or submodule their stored
// permissions do not mention. Because permissions are saved per user and a
// new module is, by definition, not in anyone's saved permissions yet, the
// value written here is the one EVERY EXISTING USER receives for it on day one.
// A `visible: true` here hands the whole company a module the moment it ships -
// including modules that carry pricing, costing or HR data. That happened with
// Quote Studio, and it took a per-user clean-up to undo.
//
// So, when adding a module or submodule, set `visible: false` and every
// Can*/Receives*/Admin* flag to false here, and the same in
// `constants/default-user-abilities.constant.ts`. Nobody gets a new module
// just because it shipped.
// ============================================================================
//
// Fully-shaped default UserAbilities. Every module + every submodule has a
// `visible: false` baseline. The various Can*/Receives*/Admin* boolean fields
// stay `undefined` at runtime, which JavaScript treats as falsy — so reads
// like `if (abilities[X][Y].CanEdit)` evaluate to false rather than throwing.
//
// This is the right side of the deep-merge in `fromAppwrite` for the user
// collection: stored partial values take precedence; anything missing falls
// back to "not visible / no permission" without crashing the UI.
//
// TypeScript enforces that every module and submodule key is present here —
// when UserAbilities evolves, missing keys become compile errors.

const visibleFalse = { visible: false } as const;

export const EMPTY_USER_ABILITIES: UserAbilities = {
    [PrismModule.Printing3d]: {
        visible: false,
        [Printing3dSubModule.Products]: { visible: false, CanEdit: false },
    },
    [PrismModule.Plastics]: {
        visible: false,
        [PlasticsSubModule.Orders]: { visible: false } as any,
        [PlasticsSubModule.OrderReview]: { visible: false } as any,
        [PlasticsSubModule.Invoices]: { visible: false, CanEdit: false, CanRelease: false },
        [PlasticsSubModule.Product]: { visible: false } as any,
        [PlasticsSubModule.HardwareItem]: { visible: false } as any,
        [PlasticsSubModule.Fixture]: { visible: false } as any,
        [PlasticsSubModule.Materials]: { visible: false } as any,
        [PlasticsSubModule.RotoSchedule]: { visible: false, CanEditK2Schedule: false } as any,
        [PlasticsSubModule.ProductionManager]: { visible: false } as any,
        [PlasticsSubModule.TaskSearch]: { visible: false } as any,
        [PlasticsSubModule.PlasticsFinishingSchedule]: { visible: false } as any,
        [PlasticsSubModule.PlasticsFinishingStation]: { visible: false, canUseInventoryTransactionDialog: false } as any,
        [PlasticsSubModule.RotoMachineOperation]: { visible: false } as any,
        [PlasticsSubModule.MaterialMixing]: { visible: false } as any,
        [PlasticsSubModule.MaterialMixingStation]: { visible: false } as any,
        [PlasticsSubModule.RotoSupervisor]: { visible: false } as any,
        [PlasticsSubModule.QualityControl]: { visible: false } as any,
        [PlasticsSubModule.QualityInspector]: { visible: false } as any,
        [PlasticsSubModule.PlasticsScrap]: { visible: false } as any,
        [PlasticsSubModule.PlasticsReporting]: { visible: false } as any,
        [PlasticsSubModule.PlasticsCalendar]: { visible: false } as any,
        [PlasticsSubModule.ComplaintLog]: { visible: false, ReceivesScheduledProductNotifications: false } as any,
        [PlasticsSubModule.PlasticsShipping]: { visible: false } as any,
        [PlasticsSubModule.ShippingManager]: { visible: false } as any,
        [PlasticsSubModule.Forecasting]: { visible: false } as any,
        [PlasticsSubModule.Molds]: { visible: false } as any,
        [PlasticsSubModule.MoldCheckups]: { visible: false } as any,
        [PlasticsSubModule.GroceryStore]: { visible: false } as any,
        [PlasticsSubModule.PlasticsStaticLabel]: { visible: false } as any,
        [PlasticsSubModule.Inventory]: { visible: false } as any,
        [PlasticsSubModule.InventoryManager]: { visible: false } as any,
        [PlasticsSubModule.Settings]: { visible: false } as any,
        [PlasticsSubModule.Suncast]: { visible: false } as any,
        [PlasticsSubModule.Clack]: { visible: false, CanEdit: false } as any,
        [PlasticsSubModule.Motionmaster]: { visible: false, CanEdit: false } as any,
        [PlasticsSubModule.Kolorcans]: { visible: false } as any,
        [PlasticsSubModule.Quoting]: { visible: false } as any,
        [PlasticsSubModule.Pricing]: { visible: false } as any,
        [PlasticsSubModule.PlasticsC3Coolers]: { visible: false } as any,
        [PlasticsSubModule.MoldRepairRequests]: { visible: false } as any,
        [PlasticsSubModule.CustomerProcesses]: { visible: true } as any,
        [PlasticsSubModule.HotList]: { visible: false, CanEdit: false } as any,
        [PlasticsSubModule.Metrics]: { visible: true } as any,
        [PlasticsSubModule.QuoteStudio]: { visible: false } as any,
    },
    [PrismModule.Automation]: {
        visible: false,
        [AutomationSubModule.FactoryNode]: { visible: false } as any,
        [AutomationSubModule.Trends]: { visible: false } as any,
        [AutomationSubModule.RobotProgramming]: { visible: false } as any,
        [AutomationSubModule.ClientDevices]: { visible: false } as any,
        [AutomationSubModule.Dashboards]: { visible: false } as any,
        [AutomationSubModule.SoftwareTasks]: { visible: false } as any,
        [AutomationSubModule.TimeTracker]: { visible: false } as any,
        [AutomationSubModule.LabelPrinting]: { visible: false } as any,
        [AutomationSubModule.EngineeringUpdateMeeting]: { visible: false } as any,
        [AutomationSubModule.KnowledgeBase]: { visible: false } as any,
        [AutomationSubModule.Schematics]: { visible: false } as any,
        [AutomationSubModule.AutomationHardwareItem]: { visible: false } as any,
        [AutomationSubModule.EngineeringProjects]: { visible: false } as any,
        [AutomationSubModule.EngineeringProjectFiles]: { visible: false, CanEdit: false },
    },
    [PrismModule.Admin]: {
        visible: false,
        [AdminSubModule.ManageUsers]: { visible: false } as any,
        [AdminSubModule.Migrations]: { visible: false } as any,
        [AdminSubModule.Collections]: { visible: false } as any,
        [AdminSubModule.OnsiteUtilities]: { visible: false } as any,
        [AdminSubModule.Utilities]: { visible: false } as any,
        [AdminSubModule.LabelPrinterGroups]: { visible: false } as any,
        [AdminSubModule.SvgPreview]: { visible: false } as any,
        [AdminSubModule.Cribbage]: { visible: false } as any,
        [AdminSubModule.CronJobs]: { visible: false } as any,
        [AdminSubModule.DataserverManagement]: { visible: false } as any,
    },
    [PrismModule.Settings]: {
        visible: false,
        [SettingsSubModule.ChangeLog]: { visible: false } as any,
        [SettingsSubModule.Display]: { visible: false } as any,
        [SettingsSubModule.BuildInfo]: { visible: false } as any,
    },
    [PrismModule.Metals]: {
        visible: false,
        [MetalsSubModule.ObsessedEngineering]: {
            visible: false,
            CanEdit: false,
            canViewAdminPanels: false,
        },
        [MetalsSubModule.MetalsHardwareItem]: { visible: false } as any,
        [MetalsSubModule.MetalsTagGenerator]: { visible: false } as any,
        [MetalsSubModule.LaserProgramming]: { visible: false } as any,
        [MetalsSubModule.LaserOperator]: { visible: false } as any,
        [MetalsSubModule.LaserScheduling]: { visible: false } as any,
        [MetalsSubModule.Products]: { visible: false } as any,
        [MetalsSubModule.Metaltrack]: { visible: false } as any,
        [MetalsSubModule.ProcessStation]: { visible: false } as any,
        [MetalsSubModule.Project]: { visible: false } as any,
        [MetalsSubModule.Settings]: { visible: false } as any,
        [MetalsSubModule.ProductionSchedule]: { visible: false } as any,
        [MetalsSubModule.Orders]: { visible: false } as any,
        [MetalsSubModule.ProductionManager]: { visible: false } as any,
        [MetalsSubModule.MetalsQuoteRequest]: { visible: false } as any,
        [MetalsSubModule.MetalsTools]: { visible: false } as any,
        [MetalsSubModule.CadViewer]: { visible: false } as any,
        [MetalsSubModule.SheetMetalStudio]: { visible: false } as any,
        [MetalsSubModule.Quoting]: { visible: false } as any,
        [MetalsSubModule.AssemblyQuoting]: { visible: false } as any,
        [MetalsSubModule.ToolRoom]: { visible: false } as any,
        // Include CanEdit so admin toggles render (undefined keys are hidden) and
        // browse "new" / edit actions can read a real boolean after deep-merge.
        [MetalsSubModule.ProductSetups]: { visible: false, CanEdit: false } as any,
        [MetalsSubModule.ToolRoomMachines]: { visible: false, CanEdit: false } as any,
        [MetalsSubModule.Inventory]: { visible: false } as any,
    },
    [PrismModule.PrismCommon]: {
        visible: false,
        [PrismCommonSubModule.Resources]: { visible: false } as any,
        [PrismCommonSubModule.Procedures]: { visible: false } as any,
        [PrismCommonSubModule.Assets]: { visible: false } as any,
        [PrismCommonSubModule.Meetings]: { visible: false } as any,
        [PrismCommonSubModule.FreightCarriers]: { visible: false } as any,
        [PrismCommonSubModule.Customer]: { visible: false, CanEdit: false, CanViewNotes: false } as any,
        [PrismCommonSubModule.Vendor]: { visible: false } as any,
        [PrismCommonSubModule.PurchaseRequests]: { visible: false } as any,
        [PrismCommonSubModule.Purchasing]: { visible: false } as any,
        [PrismCommonSubModule.PrismCommonReporting]: { visible: false } as any,
        [PrismCommonSubModule.PurchaseRequestReceiving]: { visible: false } as any,
        [PrismCommonSubModule.PurchasingDashboard]: { visible: false } as any,
        [PrismCommonSubModule.Contacts]: { visible: false } as any,
        [PrismCommonSubModule.SoftwareRequests]: { visible: false } as any,
        [PrismCommonSubModule.Maintenance]: { visible: false } as any,
        [PrismCommonSubModule.SmartJeff]: { visible: false } as any,
        [PrismCommonSubModule.Projects]: { visible: false, CanEdit: false } as any,
        [PrismCommonSubModule.EmailGroups]: { visible: false } as any,
        [PrismCommonSubModule.DirectMessages]: { visible: false } as any,
        [PrismCommonSubModule.Finance]: { visible: false } as any,
        [PrismCommonSubModule.HumanResources]: { visible: false, CanEdit: false, CanViewEmployeeRecords: false } as any,
        [PrismCommonSubModule.UpsShipments]: { visible: false } as any,
        // Include CanEdit so deep-merge exposes the key in Manage Users without
        // requiring a full "sync abilities" pass for this submodule.
        [PrismCommonSubModule.Capitalization]: { visible: false, CanEdit: false } as any,
        [PrismCommonSubModule.MoveRequests]: {
            visible: false,
            CanEdit: false,
            CanWorkMoveQueue: false,
            ReceivesMoveRequestNotifications: false,
        },
    },
    [PrismModule.C3]: {
        visible: false,
        [C3SubModule.C3Orders]: { visible: false, CanEdit: false } as any,
        [C3SubModule.C3Product]: { visible: false } as any,
        [C3SubModule.C3HardwareItem]: {
            visible: false,
            ReceivesPrintDepartmentRequestNotifications: false,
        } as any,
        [C3SubModule.C3Inventory]: { visible: false } as any,
        [C3SubModule.C3MoldedBodyCount]: { visible: false } as any,
        [C3SubModule.C3Assembly]: { visible: false } as any,
        [C3SubModule.C3StockLimits]: { visible: false } as any,
        [C3SubModule.C3Reporting]: {
            visible: false,
            CanViewInventoryThresholdReports: false,
            // Existing users who already have reporting visibility predate this
            // ability and should retain management access after the merge.
            CanManageAssemblySessionReports: true,
        },
        [C3SubModule.StickerStudio]: { visible: false } as any,
        [C3SubModule.C3ClearanceInventory]: { visible: false, CanEdit: false } as any,
        [C3SubModule.C3Graphics]: { visible: false, CanEdit: false } as any,
    },
    [PrismModule.InjectionMolding]: {
        visible: false,
        [InjectionMoldingSubModule.Orders]: {
            visible: false,
            CanEdit: false,
            CanViewDraftOrderedProducts: false,
            CanViewFutureInventoryOrders: false,
            CanReleaseOrderedProducts: false,
            CanSetReadyToShipOrderedProducts: false,
            CanCompleteOrderedProducts: false,
            CanHoldOrCancelOrderedProducts: false,
        },
        [InjectionMoldingSubModule.Products]: {
            visible: false,
            CanEdit: false,
            CanPerformInventoryTransaction: false,
            CanViewInventoryTransactions: false,
        },
        [InjectionMoldingSubModule.HardwareItems]: {
            visible: false,
            CanEdit: false,
            CanPerformInventoryTransaction: false,
            CanViewInventoryTransactions: false,
        },
        [InjectionMoldingSubModule.Materials]: {
            visible: false,
            CanEdit: false,
            CanPerformInventoryTransaction: false,
            CanViewInventoryTransactions: false,
        },
        [InjectionMoldingSubModule.QualityControl]: {
            visible: false,
            CanEdit: false,
            ReceivesQualityAlertUpdatedOrCreatedNotification: false,
        },
        [InjectionMoldingSubModule.Molds]: {
            visible: false,
            CanEdit: false,
        },
    },
};
